import React, { useState, MouseEvent, ReactNode } from "react";
import {
  Box,
  FormLabel,
  IconButton,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDownIcon from "@icons/myicons/arrow-down.svg?react";

interface Props {
  testId?: string;
  optional?: boolean;
  arrowDirection?: "up" | "down" | "none";
  startAdornment?: ReactNode;
  onClear?: () => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  readonly?: boolean;
}

export type FormInputProps = TextFieldProps & Props;

export const FakeInputField = (props: FormInputProps): React.ReactNode => {
  const {
    label = "",
    value,
    variant = "outlined",
    type = "text",
    testId = "text-input",
    optional = false,
    arrowDirection = "none",
    startAdornment,
    onClear,
    onClick,
    readonly = false,
    ...rest
  } = props;
  const [isHovered, setIsHovered] = useState(false);

  const handleOnClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClear?.();
  };

  return (
    <Box>
      {label && (
        <FormLabel>
          {typeof label === "string" ? (
            <Typography variant="body3" component="span">
              {label}
            </Typography>
          ) : (
            label
          )}
          {optional ? (
            <Typography color="textSecondary" component="span" variant="body3">
              {" "}
              (Optional)
            </Typography>
          ) : (
            ""
          )}
        </FormLabel>
      )}
      <TextField
        type={type}
        variant={variant}
        value={value}
        disabled
        autoComplete="off"
        {...rest}
        slotProps={{
          input: {
            readOnly: readonly,
            startAdornment,
            endAdornment: (
              <>
                {onClear && isHovered && (
                  <IconButton
                    onClick={handleOnClear}
                    sx={{
                      padding: "0!important",
                      height: "36px",
                      width: "36px",
                      "& svg": {
                        width: "20px",
                        height: "20px",
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
                {arrowDirection !== "none" ? (
                  <ArrowDownIcon
                    style={{
                      minWidth: "24px",
                      transform:
                        arrowDirection === "up"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                    }}
                  />
                ) : undefined}
              </>
            ),
          },
          htmlInput: {
            "data-testid": testId,
            autoComplete: "off",
            sx: {
              caretColor: "transparent",
              "&::placeholder": {
                color: "text.secondary",
                opacity: 1,
                pointerEvents: "none",
              },
              "&: MuiOutlinedInput-root": {
                pointerEvents: "none",
              },
            },
          },
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
        {...rest}
      />
    </Box>
  );
};
